import { memo, useMemo, useState } from 'react';
import cn from 'classnames';
import { Avatar, Button, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from '@heroui/react';
import { useModalAutoFocus } from '@/shared/lib/hooks';
import { CHAT_MODAL_WIDTH_CLASS , CHAT_USERNAME_SEARCH_MAX } from '../../config/constants';
import type { ChatUser } from '../../model/types';
import { IconPlus, IconSearch, IconUser } from '../icons';

export type AddParticipantsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  users: ChatUser[];
  search: string;
  onSearchChange: (value: string) => void;
  isLoading: boolean;
  isSubmitting: boolean;
  onSubmit: (userId: number) => void | Promise<void>;
};

export const AddParticipantsModal = memo(
  ({
    isOpen,
    onClose,
    users,
    search,
    onSearchChange,
    isLoading,
    isSubmitting,
    onSubmit,
  }: AddParticipantsModalProps) => {
    useModalAutoFocus(isOpen);

    const [manualSelectedUserId, setManualSelectedUserId] = useState<number | null>(null);

    const handleClose = () => {
      setManualSelectedUserId(null);
      onClose();
    };

    const selectedUserId = useMemo(() => {
      if (manualSelectedUserId && users.some((user) => user.id === manualSelectedUserId)) {
        return manualSelectedUserId;
      }

      return users[0]?.id ?? null;
    }, [manualSelectedUserId, users]);

    const selectedUser = useMemo(
      () => users.find((user) => user.id === selectedUserId) ?? null,
      [selectedUserId, users],
    );

    return (
      <Modal
        isOpen={isOpen}
        onOpenChange={(open) => {
          if (!open) handleClose();
        }}
        placement="center"
        backdrop="opaque"
        classNames={{
          base: `${CHAT_MODAL_WIDTH_CLASS} rounded-[28px] p-0`,
          closeButton: 'top-7 right-7 text-black/50 hover:bg-transparent hover:text-black',
        }}
      >
        <ModalContent className="overflow-hidden p-0">
          <ModalHeader className="px-9 pb-0 pt-8 text-[28px] font-semibold text-[#1E1E1E]">
            Add participants
          </ModalHeader>
          <ModalBody className="px-9 pb-0 pt-7">
            <div className="flex flex-col gap-6">
              <div className="flex items-center gap-3 rounded-2xl bg-[#F6F6F6] px-4 py-4">
                <IconSearch className="shrink-0 text-grey-secondary" />
                <input
                  value={search}
                  maxLength={CHAT_USERNAME_SEARCH_MAX}
                  onChange={(event) => onSearchChange(event.target.value)}
                  aria-label="Search participants"
                  placeholder="Username in academic list, or exact ID / name"
                  className="w-full bg-transparent text-base text-[#1E1E1E] outline-none placeholder:text-grey"
                />
              </div>

              <div className="max-h-[520px] overflow-y-auto pr-2">
                <ul className="flex flex-col gap-3">
                  {users.map((user) => {
                    const isSelected = user.id === selectedUserId;
                    const fullName =
                      [user.firstName, user.lastName].filter(Boolean).join(' ').trim() || user.username;

                    return (
                      <li key={user.id}>
                        <button
                          type="button"
                          onClick={() => setManualSelectedUserId(user.id)}
                          className={cn(
                            'flex w-full items-center gap-4 rounded-3xl px-4 py-3 text-left transition-colors',
                            isSelected ? 'bg-[#F1F1F1]' : 'bg-white hover:bg-[#F7F7F7]',
                          )}
                        >
                          <Avatar
                            name={fullName}
                            src={user.avatarUrl ?? undefined}
                            size="lg"
                            showFallback
                            className="h-12 w-12 shrink-0"
                          />
                          <div className="flex min-w-0 flex-1 flex-col gap-0.5 text-[#1E1E1E]">
                            <div className="flex min-w-0 items-center gap-2">
                              <IconUser className="shrink-0" />
                              <span className="truncate text-[16px] font-medium">{fullName}</span>
                            </div>
                            {user.badge ? (
                              <span className="truncate pl-6 text-[13px] text-grey-secondary">{user.badge}</span>
                            ) : (
                              <span className="truncate pl-6 text-[13px] text-grey-secondary">
                                @{user.username}
                              </span>
                            )}
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>

                {!isLoading && !users.length ? (
                  <div className="py-12 text-center text-sm text-default-500">
                    {search.trim() ? 'No matching users' : 'No users available for messaging'}
                  </div>
                ) : null}

                {isLoading ? (
                  <div className="py-12 text-center text-sm text-default-500">Loading...</div>
                ) : null}
              </div>
            </div>
          </ModalBody>
          <ModalFooter className="mt-8 flex justify-between gap-5 border-t border-[#ECECEC] px-9 py-6">
            <Button
              variant="bordered"
              className="h-[52px] min-w-[230px] rounded-[12px] border-[#1E1E1E] bg-white text-[16px] font-medium text-[#1E1E1E]"
              onPress={handleClose}
            >
              Cancel
            </Button>
            <Button
              className="h-[52px] min-w-[230px] rounded-[12px] bg-[#27245F] text-[16px] font-medium text-white"
              startContent={<IconPlus />}
              isDisabled={!selectedUser || isLoading || isSubmitting}
              isLoading={isSubmitting}
              onPress={() => {
                if (selectedUser) {
                  void onSubmit(selectedUser.id);
                }
              }}
            >
              Add
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  },
);

AddParticipantsModal.displayName = 'AddParticipantsModal';
